export const getAudioEncoding = (mimeType) => {
  if (mimeType.includes('mp4')) return 'MP3';
  if (mimeType.includes('ogg')) return 'OGG_OPUS';
  return 'WEBM_OPUS';
};

export const convertBlobToBase64 = (blob) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result.split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

export const getSupportedMimeType = (mimeTypes) => {
  for (const mimeType of mimeTypes) {
    if (MediaRecorder.isTypeSupported(mimeType)) {
      return { mimeType, options: { mimeType } };
    }
  }
  console.log('No specific MIME type supported, using default');
  return { mimeType: 'default', options: {} };
};

// Audio player instance to control playback
let currentAudio = null;


export async function synthesizeSpeech(text) {
  console.log('Synthesizing speech for text:', text);
  const response = await fetch('https://api.openai.com/v1/audio/speech', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${process.env.REACT_APP_OPENAI_KEY}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      model: 'gpt-4o-mini-tts', // Faster model with good quality
      input: text,
      voice: 'shimmer', // Informative and natural-sounding voice
      response_format: 'mp3',
      // Additional parameters for natural tone (if supported)
      prosody: {
        rate: 1.1, // Normal speaking rate
        pitch: 1.0, // Neutral pitch
      },
    }),
  });

  if (!response.ok) {
    throw new Error(`OpenAI TTS API error: ${response.statusText}`);
  }

  const blob = await response.blob();
  return URL.createObjectURL(blob);
}

export const speakText = async (text, languageCode, onStart, onEnd) => {
  try {
    // Stop any currently playing audio
    stopSpeaking();

    if (onStart) onStart();

    // Generate speech using OpenAI TTS
    const audioUrl = await synthesizeSpeech(text);

    // Create and play audio
    currentAudio = new Audio(audioUrl);

    currentAudio.onended = () => {
      if (onEnd) onEnd();
      // Clean up the object URL
      URL.revokeObjectURL(audioUrl);
      currentAudio = null;
    };

    currentAudio.onerror = (error) => {
      console.error('Audio playback error:', error);
      if (onEnd) onEnd();
      URL.revokeObjectURL(audioUrl);
      currentAudio = null;
    };

    await currentAudio.play();
  } catch (error) {
    console.error('Speech synthesis error:', error);
    if (onEnd) onEnd();
  }
};

export const stopSpeaking = () => {
  if (currentAudio) {
    currentAudio.pause();
    currentAudio.currentTime = 0;
    currentAudio = null;
  }
};

export const DEFAULT_PCM_SAMPLE_RATE = 24000;

const decodeBase64ToBytes = (audioSource) => {
  const base64 = audioSource.includes(',')
    ? audioSource.split(',')[1]
    : audioSource;
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
};

const isWavFile = (bytes) =>
  bytes.length >= 12 &&
  bytes[0] === 0x52 && // R
  bytes[1] === 0x49 && // I
  bytes[2] === 0x46 && // F
  bytes[3] === 0x46 && // F
  bytes[8] === 0x57 && // W
  bytes[9] === 0x41 && // A
  bytes[10] === 0x56 && // V
  bytes[11] === 0x45; // E

const writeString = (view, offset, string) => {
  for (let i = 0; i < string.length; i += 1) {
    view.setUint8(offset + i, string.charCodeAt(i));
  }
};

/** Wrap raw PCM s16le mono bytes in a WAV container the browser can play. */
export const wrapPcmAsWav = (
  pcmBytes,
  { sampleRate = DEFAULT_PCM_SAMPLE_RATE, numChannels = 1 } = {}
) => {
  const bytesPerSample = 2;
  const blockAlign = numChannels * bytesPerSample;
  const byteRate = sampleRate * blockAlign;
  const dataSize = pcmBytes.byteLength;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);

  writeString(view, 0, 'RIFF');
  view.setUint32(4, 36 + dataSize, true);
  writeString(view, 8, 'WAVE');
  writeString(view, 12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, bytesPerSample * 8, true);
  writeString(view, 36, 'data');
  view.setUint32(40, dataSize, true);

  new Uint8Array(buffer, 44).set(pcmBytes);
  return buffer;
};

export const playAudioData = async (
  audioSource,
  {
    mimeType = 'audio/wav',
    sampleRate = DEFAULT_PCM_SAMPLE_RATE,
    onStart,
    onEnd,
  } = {}
) => {
  try {
    stopSpeaking();

    if (!audioSource) {
      throw new Error('No audio data provided');
    }

    if (onStart) onStart();

    let audioUrl = audioSource;
    let shouldRevoke = false;

    if (!/^https?:\/\//i.test(audioSource) && !audioSource.startsWith('blob:')) {
      let bytes = decodeBase64ToBytes(audioSource);

      // API may label payload as wav/audio_base64 but return raw PCM without a RIFF header.
      if (!isWavFile(bytes) && (mimeType.includes('wav') || mimeType.includes('pcm'))) {
        bytes = new Uint8Array(wrapPcmAsWav(bytes, { sampleRate }));
        mimeType = 'audio/wav';
      }

      const blob = new Blob([bytes], { type: mimeType });
      audioUrl = URL.createObjectURL(blob);
      shouldRevoke = true;
    }

    currentAudio = new Audio(audioUrl);

    await new Promise((resolve, reject) => {
      currentAudio.onended = () => {
        if (onEnd) onEnd();
        if (shouldRevoke) URL.revokeObjectURL(audioUrl);
        currentAudio = null;
        resolve();
      };

      currentAudio.onerror = (error) => {
        console.error('Audio playback error:', error);
        if (onEnd) onEnd();
        if (shouldRevoke) URL.revokeObjectURL(audioUrl);
        currentAudio = null;
        reject(error);
      };

      currentAudio.play().catch(reject);
    });
  } catch (error) {
    console.error('Audio playback error:', error);
    if (onEnd) onEnd();
    throw error;
  }
};

export const isSpeaking = () => {
  return currentAudio && !currentAudio.paused;
};
